import { Stack, Switch, TextField, Typography } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import { addToast } from '@sophocles/features/toast';
import { DISPATCH_STATUS } from '@sophocles/hooks/commandDispatchResult';
import useCommandDispatch from '@sophocles/hooks/useCommandDispatch';

function CaptionedTextField({ title, caption, type, name }) {
  return (
    <Stack spacing={0}>
      <Field name={name}>
        {({ field }) => <TextField {...field} size="small" type={type} label={title} fullWidth />}
      </Field>
      <Typography variant="caption">{caption}</Typography>
    </Stack>
  );
}

function IntrusionCommandsDialogV2({ site, open, closeDialog, ...props }) {
  const [arm, setArm] = React.useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  site = site.toJS();

  const { dispatchCommands, submitting, activityLocked } = useCommandDispatch(site.id);

  const sensorConfig = site.relay_controller?.intrusion_sensor_configuration;

  const defaultValues = {
    rearmDelay: sensorConfig?.automatic_rearm_time_minutes ?? 60,
  };

  const submitCommand = values => {
    const commands = arm
      ? [{ key: 'ARM_INTRUSION_SENSOR', params: {} }]
      : [{ key: 'DISARM_INTRUSION_SENSOR', params: { rearm_delay: values.rearmDelay } }];
    return dispatchCommands(commands);
  };

  const getValidationSchema = () => {
    const schema = {
      rearmDelay: yup.number().required('Rearm Delay is required').max(255, `must be less than 256 minutes`),
    };

    return yup.object(schema);
  };

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={async (values, formikBag) => {
        const result = await submitCommand(values);
        if (result.status === DISPATCH_STATUS.SUCCESS) {
          dispatch(addToast({ message: t('commandCatalog.sent', 'Command queued.'), level: 'success' }));
          formikBag.resetForm();
          closeDialog();
          return;
        }
        const failure = result.failures[0];
        const message =
          result.messages[0] ??
          (failure?.message_key ? t(failure.message_key) : failure?.detail) ??
          t('commandCatalog.sendError', 'Failed to send command.');
        dispatch(addToast({ message, level: 'error' }));
      }}
      validationSchema={getValidationSchema()}
    >
      {({ isValid, submitForm, resetForm }) => (
        <Form>
          <ResponsiveDialog
            canSubmit={isValid && !submitting && !activityLocked}
            open={open}
            submitText={t('general.submit', 'Submit')}
            title={t('sites.intrusion.armDisarmTitle', 'Arm/Disarm Intrusion')}
            onClose={() => {
              resetForm();
              closeDialog();
            }}
            onSubmit={values => {
              submitForm(values);
            }}
            {...props}
          >
            <Stack spacing={1}>
              <Stack spacing={1} direction="row" alignItems="center">
                <Typography>{t('sites.intrusion.disarm', 'Disarm')}</Typography>
                <Switch
                  checked={arm}
                  disabled={activityLocked || submitting}
                  onChange={event => {
                    setArm(event.target.checked);
                  }}
                />
                <Typography>{t('sites.intrusion.arm', 'Arm')}</Typography>
              </Stack>
              {!arm && (
                <CaptionedTextField
                  title={t('sites.intrusion.rearmDelayMinutes', 'Rearm Delay (minutes)')}
                  caption={t(
                    'sites.intrusion.rearmDelayCaption',
                    'This command will temporarily disarm the intrusion sensors until the Rearm Delay expires. This command will not change the sensor configurations.',
                  )}
                  type="number"
                  name="rearmDelay"
                />
              )}
              {arm && (
                <Typography variant="caption">
                  {t(
                    'sites.intrusion.armCaption',
                    'This command will end the disarm period early. This command will not change the sensor configurations.',
                  )}
                </Typography>
              )}
            </Stack>
          </ResponsiveDialog>
        </Form>
      )}
    </Formik>
  );
}

export default IntrusionCommandsDialogV2;
